import { Box, Stack, Text, Skeleton } from '@mantine/core'
import { LineChart, BarChart } from '@mantine/charts'
import { IconChartArea } from '@tabler/icons-react'
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts'
import { ReactNode } from 'react'
import { EmptyState } from './EmptyState'

export type ChartType = 'line' | 'bar' | 'pie'

interface ChartDataPoint {
  [key: string]: string | number
}

interface DashboardChartProps {
  title: string
  type: ChartType
  data: ChartDataPoint[]
  dataKey?: string
  loading?: boolean
  height?: number
  children?: ReactNode
  options?: {
    colors?: string[]
    tooltipLabel?: string
  }
}

const DEFAULT_COLORS = ['#098d93', '#49b6d6', '#2f9e44', '#f59c1a', '#ff5b57', '#9c36b5']

export function DashboardChart({
  title,
  type,
  data,
  dataKey = Object.keys(data[0] ?? {})[1] ?? 'value',
  loading = false,
  height = 300,
  children,
  options = {},
}: DashboardChartProps) {
  const { colors = DEFAULT_COLORS, tooltipLabel } = options

  if (loading) {
    return <Skeleton height={height} radius="md" />
  }

  if (!data || data.length === 0) {
    return (
      <Stack gap={0}>
        {title && (
          <Text size="sm" fw={600} mb={16}>
            {title}
          </Text>
        )}
        <EmptyState
          bare
          size="sm"
          accent="info"
          icon={IconChartArea}
          title="Nenhum dado disponível"
        />
      </Stack>
    )
  }

  return (
    <Stack gap={0}>
      {title && (
        <Text size="sm" fw={600} mb={16}>
          {title}
        </Text>
      )}

      <Box
        style={{
          backgroundColor: 'var(--ai-bg-elevated)',
          borderRadius: 'var(--mantine-radius-md)',
          padding: '16px',
          minHeight: height,
        }}
      >
        {type === 'line' && (
          <LineChart
            h={height - 32}
            data={data}
            dataKey={dataKey}
            series={Object.keys(data[0] ?? {})
              .filter((k) => k !== 'name' && k !== 'date' && k !== 'month')
              .map((k) => ({ name: k, color: colors[0] ?? '#098d93' }))}
            curveType="linear"
            withXAxis
            withYAxis
            withTooltip
            tooltipProps={tooltipLabel ? { label: tooltipLabel } : undefined}
          />
        )}

        {type === 'bar' && (
          <BarChart
            h={height - 32}
            data={data}
            dataKey={dataKey}
            series={Object.keys(data[0] ?? {})
              .filter((k) => k !== 'name' && k !== 'date' && k !== 'month')
              .map((k, i) => ({ name: k, color: colors[i % colors.length] }))}
            withXAxis
            withYAxis
            withTooltip
            tooltipProps={tooltipLabel ? { label: tooltipLabel } : undefined}
          />
        )}

        {type === 'pie' && (
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: height - 32,
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  dataKey={dataKey}
                  nameKey={Object.keys(data[0] ?? {})[0] ?? 'name'}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  label
                >
                  {data.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={colors[index % colors.length]}
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Box>

      {children}
    </Stack>
  )
}
